import { useState, useEffect } from 'react';
import { 
  getAllPptxFiles,
  getPptxFile,
  uploadPptxFile,
  updatePptxFile,
  deletePptxFile,
  publishPptxFile,
  updateSlideDescription,
  generateDescriptionFromSlide,
  uploadThumbnail,
  deleteThumbnail,
} from '../services/pptxFiles';
import { ParsedSlide, PptxFile, PptxFileUpdate } from '../types';
import { useLang } from '../contexts/langContext';

export function usePptxFiles() {
  const { lang } = useLang();
  const [pptxFiles, setPptxFiles] = useState<PptxFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    fetchPptxFiles();
  }, []);

  const fetchPptxFiles = async () => {
    setLoading(true);
    try {
      const files = await getAllPptxFiles();
      setPptxFiles(files);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch PPTX files'));
    } finally {
      setLoading(false);
    }
  };

  const handleGetPptxFile = async (id: string) => {
    try {
      const file = await getPptxFile(id);
      return file;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to get PPTX file'));
      throw err;
    }
  };

  const handleUploadPptxFile = async (file: File) => {
    // Clear any existing error so it doesn't block the UI while uploading
    setError(null);
    try {
      const uploadedFile = await uploadPptxFile(file);
      setPptxFiles(prevFiles => [...prevFiles, uploadedFile]);
      return uploadedFile;
    } catch (err) {
      // Do NOT set global error for upload failures – let caller handle via snackbar
      throw err;
    }
  };

  const handleUpdatePptxFile = async (id: string, updates: Partial<PptxFileUpdate>) => {
    try {
      const updatedFile = await updatePptxFile(id, updates);
      setPptxFiles(prevFiles =>
        prevFiles.map(file => (file._id === id ? updatedFile : file))
      );
      return updatedFile;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update PPTX file'));
      throw err;
    }
  };

  const handleDeletePptxFile = async (id: string) => {
    try {
      await deletePptxFile(id);
      setPptxFiles(prevFiles => prevFiles.filter(file => file._id !== id));
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to delete PPTX file'));
      throw err;
    }
  };

  const handlePublishPptxFile = async (id: string) => {
    try {
      const publishedFile = await publishPptxFile(id);
      setPptxFiles(prevFiles =>
        prevFiles.map(file => (file._id === id ? publishedFile : file))
      );
      return publishedFile;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to publish PPTX file'));
      throw err;
    }
  };

  const handleUpdateSlideDescription = async (
    fileId: string,
    slideNumber: number,
    description: string
  ) => {
    try {
      const updatedSlide = await updateSlideDescription(fileId, slideNumber, description);
      setPptxFiles(prevFiles =>
        prevFiles.map(file => {
          if (file._id === fileId) {
            return {
              ...file,
              parsedSlides: file.parsedSlides.map((slide: ParsedSlide, index: number) =>
                index + 1 === slideNumber ? { ...slide, ...updatedSlide } : slide
              ),
            };
          }
          return file;
        })
      );
      return updatedSlide;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update slide description'));
      throw err;
    }
  };

  const handleGenerateDescriptionFromSlide = async (
    fileId: string,
    slideNumber: number
  ) => {
    try {
      const { description } = await generateDescriptionFromSlide(fileId, slideNumber, { lang });
      return description;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to generate slide description'));
      throw err;
    }
  };

  const handleUploadThumbnail = async (
    fileId: string,
    slideNumber: number,
    file: File
  ) => {
    try {
      await uploadThumbnail(fileId, slideNumber, file);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to upload thumbnail'));
      throw err;
    }
  };

  const handleDeleteThumbnail = async (
    fileId: string,
    slideNumber: number
  ) => {
    try {
      await deleteThumbnail(fileId, slideNumber);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to delete thumbnail'));
      throw err;
    }
  };

  return {
    pptxFiles,
    loading,
    error,
    refetch: fetchPptxFiles,
    handleGetPptxFile,
    handleUploadPptxFile,
    handleUpdatePptxFile,
    handleDeletePptxFile,
    handlePublishPptxFile,
    handleUpdateSlideDescription,
    handleGenerateDescriptionFromSlide,
    handleUploadThumbnail,
    handleDeleteThumbnail,
  };
}
